import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const API_VERSION = 'v1';
const API_TIMEOUT = 30000; // 30 seconds

// Token storage keys
const ACCESS_TOKEN_KEY = 'voting_access_token';
const REFRESH_TOKEN_KEY = 'voting_refresh_token';

/**
 * API Client class with authentication support
 */
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}/${API_VERSION}`,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor - add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle errors and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // If error is 401 and we haven't retried yet, try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = this.getRefreshToken();
            if (refreshToken) {
              const response = await axios.post(
                `${API_BASE_URL}/${API_VERSION}/auth/refresh`,
                { refreshToken }
              );

              // Extract tokens from nested response structure
              const { accessToken, refreshToken: newRefreshToken } = response.data.data || response.data;
              this.setAccessToken(accessToken);
              if (newRefreshToken) {
                this.setRefreshToken(newRefreshToken);
              }

              // Retry original request
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              }
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login
            this.clearTokens();
            // Redirect to appropriate login page based on current path
            const isAdminRoute = window.location.pathname.startsWith('/admin');
            window.location.href = isAdminRoute ? '/admin/login' : '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * GET request
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  /**
   * POST request
   */
  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  /**
   * Set access token
   */
  setAccessToken(token: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  /**
   * Set refresh token
   */
  setRefreshToken(token: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Set both tokens
   */
  setTokens(accessToken: string, refreshToken: string): void {
    this.setAccessToken(accessToken);
    this.setRefreshToken(refreshToken);
  }

  /**
   * Clear all tokens
   */
  clearTokens(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// API endpoint modules
export const authApi = {
  register: (data: {
    memberId: string;
    nationalId: string;
    email: string;
    password: string;
    confirmPassword: string;
    firstName: string;
    lastName: string;
    phone?: string;
    acceptedTerms: boolean;
  }) => apiClient.post('/auth/register', data),

  login: (data: { email: string; password: string }) =>
    apiClient.post<{ accessToken: string; refreshToken: string }>('/auth/login', data),

  logout: () => apiClient.post('/auth/logout'),

  refresh: (refreshToken: string) =>
    apiClient.post<{ accessToken: string }>('/auth/refresh', { refreshToken }),

  enableMFA: () => apiClient.post('/auth/mfa/enable'),

  verifyMFA: (code: string) => apiClient.post('/auth/mfa/verify', { code }),

  forgotPassword: (email: string) => apiClient.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string, confirmNewPassword: string) =>
    apiClient.post('/auth/reset-password', { token, newPassword, confirmNewPassword }),
};

export const voterApi = {
  getProfile: () => apiClient.get('/voters/profile'),

  getVotingHistory: () => apiClient.get('/voters/voting-history'),

  updateProfile: (data: unknown) => apiClient.patch('/voters/profile', data),
};

export const electionApi = {
  list: (params?: { status?: string }) => apiClient.get('/elections', { params }),

  getById: (id: string) => apiClient.get(`/elections/${id}`),

  getBallot: (electionId: string) => apiClient.get(`/elections/${electionId}/ballot`),

  getResults: (electionId: string) => apiClient.get(`/elections/${electionId}/results`),
};

export const votingApi = {
  hasVoted: (electionId: string) =>
    apiClient.get(`/voting/elections/${electionId}/has-voted`),

  requestBlindSignature: (electionId: string, data: unknown) =>
    apiClient.post(`/elections/${electionId}/request-blind-signature`, data),

  submitVote: (electionId: string, data: unknown) =>
    apiClient.post(`/elections/${electionId}/submit-vote`, data),

  verifyReceipt: (receiptCode: string) =>
    apiClient.get(`/verify-receipt?code=${receiptCode}`),

  saveVoteReference: (data: { electionId: string; votes: any; receiptCode: string }) =>
    apiClient.post(`/voting/my-vote-reference`, data),

  getMyVote: (electionId: string) =>
    apiClient.get(`/voting/elections/${electionId}/my-vote`),
};

export const auditApi = {
  getLogs: (params?: { startDate?: string; endDate?: string; actionType?: string }) =>
    apiClient.get('/audit/logs', { params }),

  getParticipationReport: (electionId: string) =>
    apiClient.get(`/audit/reports/participation?electionId=${electionId}`),
};

// Error handling helper
export function handleApiError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return axiosError.response?.data?.message || axiosError.message || 'Error desconocido';
  }
  return 'Error desconocido';
}
